import LocalStorageModel from "../prototypes/LocalStorageModel";
import { urlWithSyncVersion } from "@src/backbone/prototypes/ObjectPrototype";
import { GATSBY_ENV_TIMEZONE } from "@src/utils/constants";
import { IProductModifier } from "@src/backbone/model/StoreModel";

// Item d'une catégorie du catalogue dynamique (cf. RUN-7707 back)
export type CatalogItem =
  | { type: "product"; id_product: string }
  | { type: "banner"; id_banner: string };

// Catégorie du catalogue dynamique, arborescence récursive (sous-catégories)
export type CatalogCategory = {
  id_category: string;
  items: CatalogItem[];
  categories?: CatalogCategory[];
};

type StoreContextModelAttributes = {
  _fetchFailed?: boolean;
  id_store: string;
  vae: boolean;
  date?: Date;
  date_url: string;
  mode: string;
  segmentation: number;
  expire_date?: string;
  products: { id_product: string; available: boolean; price: number }[];
  payment_modes: {
    id_payment_mode: string;
    params?: {
      brand?: string[];
    };
  }[];
  // Arborescence catégories/items résolue côté back (ordre, boutique, VAE/VAD, temporalité)
  catalog?: CatalogCategory[];
};

// Formate un Date en "YYYY-MM-DD-HH-MM" dans la timezone du site (GATSBY_ENV_TIMEZONE via Intl),
// ou en UTC si aucune timezone n'est configurée (comportement d'origine).
function formatDateUrlInSiteTimezone(date: Date): string {
  if (GATSBY_ENV_TIMEZONE) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: GATSBY_ENV_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
    const hour = String(Number(get("hour")) % 24).padStart(2, "0");
    return `${get("year")}-${get("month")}-${get("day")}-${hour}-${get(
      "minute"
    )}`;
  }
  // Fallback : comportement original (UTC via toISOString)
  return date.toISOString().replace(/^(.{10})T((..):(..)).*/, "$1-$3-$4");
}

class StoreContextModel extends LocalStorageModel<
  Partial<StoreContextModelAttributes>
> {
  name = "StoreContextModel";
  idAttribute = "id_store";
  route = "apiStoreContextModel";
  collections = undefined;
  remoteCache = "cache";
  messages: any;
  products?: StoreContextModelAttributes["products"];
  defaults() {
    return {
      id_store: "",
      vae: false,
      mode: "vad",
      date_url: "now",
      segmentation: 0,
      products: [],
      payment_modes: [],
      catalog: [],
    };
  }

  initialize(attributes?: any, options?: any) {
    super.initialize(attributes, options);
    this.set(
      {
        mode: !!attributes.vae ? "vae" : "vad",
        date_url: !!attributes.date
          ? formatDateUrlInSiteTimezone(attributes.date)
          : "now",
      },
      { silent: true }
    );
  }

  getProduct(id_product: string) {
    const products: IProductModifier[] = this.get("products") ?? [];
    return products.find((product) => product.id_product === id_product);
  }
}

StoreContextModel.prototype.name = "StoreContextModel";
StoreContextModel.prototype.route = "apiStoreContextModel";
StoreContextModel.prototype.idAttribute = "id_store";
StoreContextModel.prototype.url = function () {
  return urlWithSyncVersion(
    `/storecontext/${this.id}/${this.get("mode")}/${this.get("date_url")}/${this.get("segmentation")}`
  );
};
StoreContextModel.prototype.remoteCache = "cache";
export default StoreContextModel;
